import React, { useEffect, useMemo, useState } from 'react';
import { PageContainer, PageHeader, ContentCard, EmptyState } from '../../../../components/layout';
import { Table } from '../../../../components/data';
import Button from '../../../../components/common/Button';
import Badge from '../../../../components/common/Badge';
import Input from '../../../../components/common/Input';
import { ConfirmModal } from '../../../../components/common/ConfirmModal';
import { ToastContainer, useToast } from '../../../../components/common/Toast';
import { LuAlertCircle } from 'react-icons/lu';
import { FaPlus, FaEdit, FaSearch, FaBan, FaCheck, FaSyncAlt } from 'react-icons/fa';
import { translate, getFormatDate } from '../../../../utils/Common';
import { verifyAccessViewPermissions } from '../../../../config/permissions';
import { SETTINGS_EXCEPTIONS_PATH } from '../../../../config/bliPermissionsRuntime';
import ExceptionFormModal from './ExceptionFormModal';
import SyncScopeModal from './SyncScopeModal';
import { useExceptions } from './useExceptions';
import { usePlatforms } from '../../../../hooks/usePlatforms';
import styles from './Exceptions.module.css';

const STATUS_FILTERS = ['all', 'active', 'inactive'];

/**
 * ConfExceptions catalog: the incidents a delivery agent can report from a locker. It is a global
 * catalog (no platform), and every write is pushed to the whole locker network by api-sync, so a
 * failed push is surfaced as a warning and can be retried with the sync button.
 */
const Exceptions = () => {
  /**
   * Same gate as PermissionsEditor: the catalog stores the Settings module under either path, and
   * this screen lives under it, so both roots are tried before giving up.
   */
  const canDo = (action) =>
    ['/dashboard/settings', '/dashboard/configuracion', SETTINGS_EXCEPTIONS_PATH].some((path) =>
      verifyAccessViewPermissions([action], { path })
    );

  const canCreate = canDo('create');
  const canUpdate = canDo('update');

  const {
    exceptions,
    counts,
    loading,
    error,
    permissionDenied,
    saving,
    syncing,
    load,
    createException,
    updateException,
    setExceptionStatus,
    syncAll,
  } = useExceptions();

  const { toasts, showSuccess, showError, showInfo, removeToast } = useToast();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  /** Exception queued for the inactivate / reactivate confirmation. */
  const [statusTarget, setStatusTarget] = useState(null);
  const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return exceptions
      .filter((e) => {
        if (statusFilter === 'active' && e.status !== 1) return false;
        if (statusFilter === 'inactive' && e.status === 1) return false;
        if (!term) return true;
        return [e.code, e.description, e.engName, e.espName, e.porName].some((v) =>
          String(v || '').toLowerCase().includes(term)
        );
      })
      .map((e) => ({ ...e, id: e.exceptionId }));
  }, [exceptions, search, statusFilter]);

  /** A write always commits the row; only the locker push can fail, and that is recoverable. */
  const reportWrite = (result, successKey) => {
    if (!result.ok) {
      showError(translate(`dashboard.configuration.exceptions.errors.${result.error}`) || result.error);
      return false;
    }
    showSuccess(translate(successKey));
    if (result.syncFailed) showError(translate('dashboard.configuration.exceptions.syncWarning'), 8000);
    return true;
  };

  const handleSubmit = async (draft) => {
    const result = editing?.exceptionId
      ? await updateException(editing.exceptionId, draft)
      : await createException(draft);

    const ok = reportWrite(
      result,
      editing?.exceptionId
        ? 'dashboard.configuration.exceptions.updated'
        : 'dashboard.configuration.exceptions.created'
    );

    if (ok) {
      setFormOpen(false);
      setEditing(null);
    }
  };

  const handleToggleStatus = async () => {
    if (!statusTarget) return;
    const activating = statusTarget.status !== 1;
    const result = await setExceptionStatus(statusTarget.exceptionId, activating);

    reportWrite(
      result,
      activating
        ? 'dashboard.configuration.exceptions.activated'
        : 'dashboard.configuration.exceptions.inactivated'
    );
    setStatusTarget(null);
  };

  // platformIds = null -> whole fleet; [ids] -> full catalog only to those lockers
  const handleSyncAll = async (platformIds) => {
    setSyncConfirmOpen(false);
    showInfo(translate('dashboard.configuration.exceptions.syncStarted'));
    const result = await syncAll(platformIds);
    if (result.ok) showSuccess(translate('dashboard.configuration.exceptions.syncSuccess'));
    else showError(translate('dashboard.configuration.exceptions.syncError'));
  };

  // locker list for the scope picker, fetched once when the sync modal first opens
  const { getAllPlatformsClient } = usePlatforms();
  const [lockerList, setLockerList] = useState(null);
  // Starts true: between the first render and the effect firing, a false
  // flag with an empty list paints the "nothing here" state for a frame,
  // which reads as the screen finding nothing and then changing its mind.
  const [lockersLoading, setLockersLoading] = useState(true);
  useEffect(() => {
    if (!syncConfirmOpen || lockerList !== null) return;
    setLockersLoading(true);
    getAllPlatformsClient()
      .then((result) => setLockerList(Array.isArray(result) ? result : []))
      .catch(() => setLockerList([]))
      .finally(() => setLockersLoading(false));
  }, [syncConfirmOpen, lockerList, getAllPlatformsClient]);

  const columns = useMemo(
    () => [
      {
        key: 'code',
        label: translate('dashboard.configuration.exceptions.code'),
        sortable: true,
        width: '200px',
        render: (row) => <code className={styles.code}>{row.code || '-'}</code>,
      },
      {
        key: 'engName',
        label: translate('dashboard.configuration.exceptions.names'),
        render: (row) => (
          <div className={styles.namesCell}>
            <span className={styles.nameLine}>
              <span className={styles.langTag}>EN</span> {row.engName || '-'}
            </span>
            <span className={styles.nameLine}>
              <span className={styles.langTag}>ES</span> {row.espName || '-'}
            </span>
            <span className={styles.nameLine}>
              <span className={styles.langTag}>PT</span> {row.porName || '-'}
            </span>
          </div>
        ),
      },
      {
        key: 'description',
        label: translate('global.description'),
        render: (row) => <span className={styles.description}>{row.description || '-'}</span>,
      },
      {
        key: 'customerFacing',
        label: translate('dashboard.configuration.exceptions.customerFacing'),
        sortable: true,
        width: '150px',
        render: (row) => (
          <Badge variant={row.customerFacing ? 'info' : 'neutral'} size="sm">
            {row.customerFacing ? translate('global.yes') : translate('global.no')}
          </Badge>
        ),
      },
      {
        key: 'status',
        label: translate('global.status'),
        sortable: true,
        width: '120px',
        render: (row) => (
          <Badge variant={row.status === 1 ? 'success' : 'neutral'} size="sm">
            {row.status === 1
              ? translate('dashboard.configuration.exceptions.active')
              : translate('global.inactive')}
          </Badge>
        ),
      },
      {
        key: 'createdAt',
        label: translate('global.dateCreated'),
        sortable: true,
        width: '120px',
        render: (row) => (row.createdAt ? getFormatDate(row.createdAt, true) : '-'),
      },
      {
        key: 'actions',
        label: translate('global.action'),
        width: '190px',
        render: (row) => (
          <div className={styles.rowActions}>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<FaEdit size={13} />}
              disabled={!canUpdate}
              onClick={(e) => {
                e.stopPropagation();
                setEditing(row);
                setFormOpen(true);
              }}
            >
              {translate('global.edit')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={row.status === 1 ? <FaBan size={13} /> : <FaCheck size={13} />}
              disabled={!canUpdate}
              onClick={(e) => {
                e.stopPropagation();
                setStatusTarget(row);
              }}
            >
              {row.status === 1
                ? translate('dashboard.configuration.exceptions.inactivate')
                : translate('dashboard.configuration.exceptions.activate')}
            </Button>
          </div>
        ),
      },
    ],
    [canUpdate]
  );

  if (permissionDenied) {
    return (
      <PageContainer maxWidth="full" padding="md">
        <EmptyState
          icon={<LuAlertCircle size={64} />}
          title={translate('dashboard.configuration.exceptions.title')}
          description={translate('dashboard.configuration.exceptions.permissionDenied')}
        />
      </PageContainer>
    );
  }

  const isEmpty = !loading && rows.length === 0;

  return (
    <PageContainer maxWidth="full" padding="md">
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <PageHeader
        title={translate('dashboard.configuration.exceptions.title')}
        subtitle={
          loading
            ? translate('global.loading')
            : `${counts.total} ${translate('global.results')} · ${counts.active} ${translate(
                'dashboard.configuration.exceptions.active'
              )} · ${counts.inactive} ${translate('global.inactive')}`
        }
        icon={<LuAlertCircle size={28} />}
        actions={
          <div className={styles.headerActions}>
            <Button
              variant="outline"
              size="md"
              leftIcon={<FaSyncAlt size={15} />}
              loading={syncing}
              disabled={syncing || !canUpdate || counts.total === 0}
              onClick={() => setSyncConfirmOpen(true)}
            >
              {translate('dashboard.configuration.exceptions.sync')}
            </Button>
            {canCreate && (
              <Button
                variant="primary"
                size="md"
                leftIcon={<FaPlus size={14} />}
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                {translate('dashboard.configuration.exceptions.new')}
              </Button>
            )}
          </div>
        }
      />

      <div className={styles.toolbar}>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={translate('dashboard.configuration.exceptions.searchPlaceholder')}
          leftIcon={<FaSearch size={14} />}
          size="md"
          className={styles.searchInput}
        />
        <div className={styles.statusFilters}>
          {STATUS_FILTERS.map((key) => (
            <button
              type="button"
              key={key}
              className={`${styles.filterChip} ${statusFilter === key ? styles.filterChipActive : ''}`}
              onClick={() => setStatusFilter(key)}
            >
              {translate(`dashboard.configuration.exceptions.filter.${key}`)}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <ContentCard>
          <div className={styles.errorBanner}>
            <strong>{translate('global.error')}:</strong>{' '}
            {translate(`dashboard.configuration.exceptions.errors.${error}`) || error}
          </div>
        </ContentCard>
      )}

      {isEmpty ? (
        <EmptyState
          icon={<LuAlertCircle size={64} />}
          title={translate('dashboard.configuration.exceptions.title')}
          description={
            exceptions.length === 0
              ? translate('dashboard.configuration.exceptions.empty')
              : translate('dashboard.configuration.exceptions.emptyFiltered')
          }
        />
      ) : (
        <Table
          columns={columns}
          data={rows}
          loading={loading}
          emptyMessage={translate('dashboard.configuration.exceptions.emptyFiltered')}
          getRowClassName={(row) => (row.status === 1 ? '' : styles.inactiveRow)}
          hoverable
        />
      )}

      <ExceptionFormModal
        isOpen={formOpen}
        exception={editing}
        saving={saving}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSubmit}
      />

      <ConfirmModal
        isOpen={Boolean(statusTarget)}
        onClose={() => setStatusTarget(null)}
        onConfirm={handleToggleStatus}
        isLoading={saving}
        variant={statusTarget?.status === 1 ? 'danger' : 'primary'}
        title={
          statusTarget?.status === 1
            ? translate('dashboard.configuration.exceptions.inactivateTitle')
            : translate('dashboard.configuration.exceptions.activateTitle')
        }
        message={
          statusTarget?.status === 1
            ? translate('dashboard.configuration.exceptions.inactivateConfirm')
            : translate('dashboard.configuration.exceptions.activateConfirm')
        }
        confirmText={
          statusTarget?.status === 1
            ? translate('dashboard.configuration.exceptions.inactivate')
            : translate('dashboard.configuration.exceptions.activate')
        }
        cancelText={translate('global.cancel')}
      />

      <SyncScopeModal
        isOpen={syncConfirmOpen}
        onClose={() => setSyncConfirmOpen(false)}
        onConfirm={handleSyncAll}
        isLoading={syncing}
        platforms={lockerList}
        platformsLoading={lockersLoading}
      />
    </PageContainer>
  );
};

export default Exceptions;
